import { useCallback } from 'react';

import type { ProductKeys3P } from '../../../common/constants/products';
import type {
	ConnectorSource,
	ThirdPartyConfigsBootstrap,
} from '../../../common/constants/schemas/3p-config';
import { useAppContext } from '../../../common/ui/app-context';
import {
	type OAuthSource,
	onOAuthCompleted,
	onOAuthFailed,
	onOAuthStarted,
} from '../../../common/utils/events/oauth-events';
import { serveSearchPageRelayExp } from '../../../common/utils/experiments';
import { getExtensionData } from '../../../common/utils/relay/get-extension-data';
import { isProductImpersonated } from '../../../common/utils/search-result-utils';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../analytics';
import { usePrimaryProduct } from '../../store/bootstrap';

type OAuthEventProps = {
	cloudId: string;
	product: string;
	connectorSource?: ConnectorSource;
	isOnSearchDialog?: boolean;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	extraAttributes?: Record<string, unknown>;
};

export const useFireOAuthStarted: ({
	cloudId,
	product,
	connectorSource,
	isOnSearchDialog,
	thirdPartyConfigs,
}: OAuthEventProps) => (oAuthSource: OAuthSource) => void = ({
	cloudId,
	product,
	connectorSource,
	isOnSearchDialog,
	thirdPartyConfigs,
	extraAttributes,
}: OAuthEventProps) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const [primaryProduct] = usePrimaryProduct();
	const { queryParams } = useAppContext();

	const callback = useCallback(
		(oAuthSource: OAuthSource) => {
			fireAnalyticsEvent(
				onOAuthStarted({
					cloudId,
					product,
					connectorSource,
					commonAttributes: {
						...commonAttributes,
						...(primaryProduct && serveSearchPageRelayExp(primaryProduct, false, queryParams)
							? getExtensionData(commonAttributes.isCQL)
							: {}),
					},
					nonPrivacySafeAttributes,
					isOnSearchDialog,
					oAuthSource,
					isImpersonated: isProductImpersonated(product as ProductKeys3P, thirdPartyConfigs),
					extraAttributes,
				}),
			);
		},
		[
			fireAnalyticsEvent,
			cloudId,
			product,
			connectorSource,
			commonAttributes,
			nonPrivacySafeAttributes,
			isOnSearchDialog,
			thirdPartyConfigs,
			primaryProduct,
			queryParams,
			extraAttributes,
		],
	);
	return callback;
};

export const useFireOAuthCompleted: ({
	cloudId,
	product,
	connectorSource,
	isOnSearchDialog,
	thirdPartyConfigs,
}: OAuthEventProps) => (oAuthSource: OAuthSource) => void = ({
	cloudId,
	product,
	connectorSource,
	isOnSearchDialog,
	thirdPartyConfigs,
	extraAttributes,
}: OAuthEventProps) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const callback = useCallback(
		(oAuthSource: OAuthSource) => {
			fireAnalyticsEvent(
				onOAuthCompleted({
					cloudId,
					product,
					connectorSource,
					commonAttributes,
					nonPrivacySafeAttributes,
					isOnSearchDialog,
					oAuthSource,
					isImpersonated: isProductImpersonated(product as ProductKeys3P, thirdPartyConfigs),
					extraAttributes,
				}),
			);
		},
		[
			fireAnalyticsEvent,
			cloudId,
			product,
			connectorSource,
			commonAttributes,
			nonPrivacySafeAttributes,
			isOnSearchDialog,
			thirdPartyConfigs,
			extraAttributes,
		],
	);
	return callback;
};

export const useFireOAuthFailed: ({
	cloudId,
	product,
	connectorSource,
	isOnSearchDialog,
	thirdPartyConfigs,
}: OAuthEventProps) => (oAuthSource: OAuthSource) => void = ({
	cloudId,
	product,
	connectorSource,
	isOnSearchDialog,
	thirdPartyConfigs,
	extraAttributes,
}: OAuthEventProps) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const callback = useCallback(
		(oAuthSource: OAuthSource) => {
			fireAnalyticsEvent(
				onOAuthFailed({
					cloudId,
					product,
					connectorSource,
					commonAttributes,
					nonPrivacySafeAttributes,
					isOnSearchDialog,
					oAuthSource,
					isImpersonated: isProductImpersonated(product as ProductKeys3P, thirdPartyConfigs),
					extraAttributes,
				}),
			);
		},
		[
			fireAnalyticsEvent,
			cloudId,
			product,
			connectorSource,
			commonAttributes,
			nonPrivacySafeAttributes,
			isOnSearchDialog,
			thirdPartyConfigs,
			extraAttributes,
		],
	);
	return callback;
};
